import { queryOptions } from '@tanstack/react-query';
import { api } from '../../lib/api';

/**
 * Who the composer's `@` picker may offer.
 *
 *   GET /workspaces/:ws/mentionable-people -> { items: MentionCandidate[] }
 *
 * The list that comes back is already the answer — see
 * `mentions.service.ts`'s header on the visibility rule. A client-role caller
 * never receives a Noorix name in this payload at all, so there is nothing
 * for the picker to filter or hide; it only has to render what it was given.
 *
 * Not in `lib/queryKeys.ts` (out of bounds for this change — see the task's
 * file-ownership list) but the key still follows that file's hard rule:
 * every workspace-scoped key starts `['ws', workspaceId, …]`, so an
 * `invalidateQueries({ queryKey: ['ws', id] })` elsewhere still reaches it.
 */

export interface MentionCandidate {
  readonly id: string;
  readonly name: string;
  readonly email: string;
}

function mentionablePeoplePath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/mentionable-people`;
}

export function fetchMentionablePeople(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<{ items: MentionCandidate[] }> {
  return api.get<{ items: MentionCandidate[] }>(mentionablePeoplePath(workspaceId), signal);
}

export function mentionablePeopleQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: ['ws', workspaceId, 'composer', 'mentionable-people'] as const,
    queryFn: ({ signal }) => fetchMentionablePeople(workspaceId, signal),
    // The roster changes rarely enough (someone joins or leaves the
    // workspace) that refetching on every composer mount would be wasted
    // work; five minutes matches the profile query's staleTime in useAuth.ts.
    staleTime: 5 * 60_000,
  });
}
